// Rolagem da grade até a hora atual (A-3 item d).
//
// ★★★ O PROBLEMA: a grade da semana tem ~20 linhas de horário e cabe ~10 na
// tela. Abrindo em 08:00, quem olha a agenda às 15h rola todo dia, à mão, para
// chegar onde o trabalho está.
//
// ★ A linha escolhida é a ÚLTIMA cujo horário já começou — não a mais próxima.
// Às 14h50, com linhas de 14:30 e 15:00, a vez é da de **14:30**: a consulta das
// 14:30 pode ainda estar em curso, a das 15:00 não começou. Arredondar para a
// mais próxima esconderia a linha que está acontecendo agora.
//
// ⚠ A função é PURA e recebe `agora`. `new Date()` dentro dela faria o teste
// depender da hora em que roda — a mesma armadilha do fuso que a E7-b mediu nas
// Functions.

const DOIS = (n) => String(n).padStart(2, '0');

/** `HH:MM` de um `Date`, no fuso do navegador (que é o da clínica). */
export const horaDoRelogio = (agora = new Date()) => `${DOIS(agora.getHours())}:${DOIS(agora.getMinutes())}`;

/**
 * A linha onde a grade deve parar, ou `null` quando não há linha nenhuma.
 *
 * ★ Antes da primeira linha → a PRIMEIRA (de manhã cedo a grade começa no topo,
 *   que é onde o dia começa). Depois da última → a ÚLTIMA.
 */
export function horaDeRolagem(linhas = [], agora = new Date()) {
  if (!Array.isArray(linhas) || linhas.length === 0) return null;
  const agoraHHMM = horaDoRelogio(agora);
  let escolhida = linhas[0];
  for (const hora of linhas) {
    if (String(hora) <= agoraHHMM) escolhida = hora;
    else break;
  }
  return escolhida;
}

/**
 * Rola o envelope da grade até a linha da hora, deixando-a logo abaixo do
 * cabeçalho fixo.
 *
 * ⚠⚠ `scrollTop` CRU, e não `scrollIntoView`: o segundo rola o ANCESTRAL mais
 * próximo que der — na prática a página inteira —, e a grade sairia da tela
 * levando o cabeçalho da Agenda com ela. Aqui só o `.agenda-grade-envolve` se
 * move.
 *
 * ★ Devolve `true` quando rolou. Linha ausente (a hora não está na grade deste
 * período) **não é erro**: a tela fica onde estava.
 */
/**
 * ★★★ O MOTOR DAS DUAS ROLAGENS — e ele é CONSERTO DE DEFEITO MEDIDO (12/out/2026).
 *
 * ⚠⚠⚠ A VERSÃO ANTERIOR USAVA `offsetLeft`/`offsetTop`, E ESTAVA ERRADA.
 * `offsetLeft` é relativo ao **`offsetParent`** — o ancestral POSICIONADO mais
 * próximo —, **não** à caixa de rolagem. `.agenda-grade-envolve` não tem
 * `position`, então o `offsetParent` era o `body`: o número vinha com a largura
 * do menu lateral e o padding da página somados.
 *
 * ★★★ MEDIDO EM CHROME DE VERDADE, com a semana cheia: numa SEGUNDA — quando hoje
 * é a PRIMEIRA coluna e o certo é **não rolar** — o `scrollLeft` ia para **261px**
 * (= 325 do `offsetLeft` até o `body` − 64 da calha), e a coluna de hoje ficava
 * **atrás da calha**. ⚠ É exatamente o que o dono viu: *"a rolagem andou demais"*.
 *
 * ⚠⚠ E O TESTE EM jsdom PASSAVA, porque ele **plantava `offsetLeft`** — o mesmo
 * engano que o código fazia. ★★★ Dois erros concordando não são uma prova: foi o
 * navegador real que separou os dois.
 *
 * ★ `getBoundingClientRect` é relativo à JANELA e já reflete a rolagem atual, então
 * a conta vira um DESLOCAMENTO sobre o `scroll` que já existe — independente de
 * `offsetParent`, de padding e de onde a caixa está na página.
 *
 * ★★ `fixo` é a parte que não pode ficar escondida atrás: o `thead` (sticky no
 * topo) para a vertical, a calha de horário (sticky à esquerda) para a horizontal.
 * ⚠ Sem descontá-la, a linha/coluna para **debaixo** dela.
 */
function deslocar(envolve, propriedadeDeScroll, borda, alvo, fixo) {
  const caixa = envolve.getBoundingClientRect?.();
  const dele = alvo.getBoundingClientRect?.();
  if (!caixa || !dele) return false;
  const tamanhoDoFixo = fixo?.getBoundingClientRect?.()
    ? (borda === 'left'
      ? fixo.getBoundingClientRect().width
      : fixo.getBoundingClientRect().height)
    : 0;
  /**
   * ⚠⚠⚠ A BORDA DA CAIXA ENTRA NA CONTA — e isto é conserto de 1px que o teste em
   * Chrome pegou (12/out), depois de eu já ter trocado `offsetLeft` por
   * `getBoundingClientRect`.
   *
   * ★ `getBoundingClientRect()` devolve a borda EXTERNA; o conteúdo rolável começa
   * `clientLeft`/`clientTop` adentro (a borda de 1px de `.agenda-grade-envolve`).
   * ⚠⚠ Sem descontá-la o alvo parava **1px atrás** do fixo — e 1px é suficiente:
   * medido numa SEGUNDA a 1000px, `scrollLeft` ia a **1** e a asserção
   * "hoje atrás da calha" ficava VERDADEIRA. ★★★ O erro de 261px era grosseiro; este
   * de 1px passaria por qualquer olho e por qualquer teste frouxo.
   */
  const bordaDaCaixa = borda === 'left' ? (envolve.clientLeft ?? 0) : (envolve.clientTop ?? 0);
  const limite = caixa[borda] + bordaDaCaixa + tamanhoDoFixo;
  const delta = dele[borda] - limite;
  // ★ Nunca negativo: alvo já visível no começo deixa a caixa onde está.
  envolve[propriedadeDeScroll] = Math.max(0, (envolve[propriedadeDeScroll] ?? 0) + delta);
  return true;
}

export function rolarAteHora(envolve, hora) {
  if (!envolve || !hora) return false;
  // ★ Seletor por atributo, não por índice: a ordem das linhas é da grade, e
  // amarrar a rolagem a uma posição a quebraria no primeiro dia com horário
  // diferente (o sábado tem 12:00, o dia útil não).
  const linha = envolve.querySelector?.(`[data-hora="${hora}"]`);
  if (!linha) return false;
  const cabecalho = envolve.querySelector?.('thead');
  return deslocar(envolve, 'scrollTop', 'top', linha, cabecalho);
}

export function rolarAteHoje(envolve) {
  if (!envolve) return false;
  const coluna = envolve.querySelector?.('thead th.agenda-hoje');
  if (!coluna) return false;
  const calha = envolve.querySelector?.('thead th.agenda-calha');
  return deslocar(envolve, 'scrollLeft', 'left', coluna, calha);
}
